<template>
  <BaseCard style="scroll-margin-top: calc(4rem + var(--sat))">
    <BaseForm
      ref="formComponent"
      class="space-y-5"
      :method="onFormSubmit"
      :reset-on-submit="false"
    >
      <div class="font-bold md:text-lg">
        {{ $t('showcases.company_data') }}
      </div>
      <div class="flex gap-6 flex-wrap md:flex-nowrap">
        <BaseFormField>
          <template #label> {{ $t('profile.company') }}*</template>
          <template #input>
            <BaseSelectAsync
              v-model="form.company_id"
              :placeholder="$t('profile.select_or_create_company')"
              :search-placeholder="$t('profile.search_or_create_company')"
              name="company_id"
              reset-on-open
              :fetch-on-open="false"
              :initial="initialCompany"
              :fetch="getCompaniesOptions"
              :disabled="isDisabled"
              required
            >
              <template #option="{ option }">
                <span class="flex-1 truncate" :title="option.label">
                  {{ option.label }}
                  <span
                    v-if="option.label === option.value"
                    class="text-sm text-gray-400"
                  >
                    {{ $t('profile.new_company') }}
                  </span>
                </span>
              </template>
            </BaseSelectAsync>
          </template>
        </BaseFormField>
        <BaseFormField>
          <template #label> {{ $t('companies.employees') }}*</template>
          <template #input>
            <BaseSelect
              v-model="form.size"
              name="size"
              :placeholder="$t('companies.select_employees')"
              :options="catalogs.companySizes"
              :disabled="isDisabled"
              :loading="isLoading"
              required
            />
          </template>
        </BaseFormField>
      </div>
      <input type="submit" hidden />
    </BaseForm>
  </BaseCard>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import BaseForm from '~/components/base/BaseForm.vue'
import BaseSelectAsync from '~/components/base/controls/BaseSelectAsync.vue'
import type { CompanyUpdate } from '~/models/companies'
import type { UserProfileUpdate } from '~/models/users'
import { useCompanies } from '~/stores/companies'
import { useUsers } from '~/stores/users'
import { useAuth } from '~/stores/auth'
import { useNotifications } from '~/stores/notifications'
import type { OptionInterface } from '~/components/base/controls/use-simple-input'
import { toAutocomplete } from '~/utils/autocomplete'

const { pushNotification } = useNotifications()
const usersStore = useUsers()
const authStore = useAuth()
const companiesStore = useCompanies()
const { t } = useI18n()
const catalogs = useCatalog()

interface Props {
  externalProcessing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  externalProcessing: false,
})

const emit = defineEmits<{
  (e: 'loading', value: boolean): void
  (e: 'companyChange', value: number | null): void
  (e: 'submitRequest', value: void): void
}>()

defineExpose({
  submit,
  validate,
})

const isLoading = ref(false)
const internalProcessing = ref(false)

const isDisabled = computed(() => {
  return props.externalProcessing || internalProcessing.value
})

const formComponent = ref<InstanceType<typeof BaseForm> | null>(null)

type Form = Pick<UserProfileUpdate & CompanyUpdate, 'company_id' | 'size'>

const form = reactive<Form>({
  company_id: undefined,
  size: undefined,
})

function fillForm() {
  fillFormProfileData()
  fillFormCompanyData()
}

fillForm()

function fillFormProfileData(): void {
  form.company_id = authStore.profile?.company_id
}

function fillFormCompanyData(): void {
  form.size = companiesStore.company?.size
}

watch(isLoading, (value) => {
  emit('loading', value)
})

watch(
  () => form.company_id,
  async (companyId) => {
    emit('companyChange', (!isNewCompany(companyId) && companyId) || null)

    await fetchCompany()
  }
)

onServerPrefetch(async () => {
  await fetchCompany()
})

onMounted(async () => {
  await fetchCompany()
})

function isNewCompany(companyId?: number | string | null): boolean {
  return typeof companyId === 'string'
}

async function fetchCompany(): Promise<void> {
  const companyId = form.company_id

  if (companyId && !isNewCompany(companyId)) {
    isLoading.value = true
    await companiesStore.fetchCompany(companyId)
    isLoading.value = false
  } else {
    companiesStore.resetCompany()
  }

  fillFormCompanyData()
}

function getCompaniesOptions(query: string) {
  return companiesStore.getCompaniesOptions(query, true)
}

const initialCompany = ref<OptionInterface | undefined>(
  authStore.profile?.company
    ? toAutocomplete(authStore.profile.company)
    : undefined
)

function onFormSubmit(): void {
  emit('submitRequest')
}

async function validate(): Promise<boolean> {
  return (await formComponent.value?.validate())?.valid || false
}

async function submit(): Promise<void> {
  const needCreateCompany = isNewCompany(form.company_id)

  if (!form.company_id || internalProcessing.value || !(await validate())) {
    return
  }

  try {
    internalProcessing.value = true

    if (needCreateCompany) {
      await companiesStore.createCompany({
        name: String(form.company_id),
        size: form.size,
        geo: authStore.profile?.country_id || undefined,
      })

      form.company_id = companiesStore.company?.id
    }

    await authStore.updateProfile({
      company_id: form.company_id,
    })
    await usersStore.reloadUser()

    if (!needCreateCompany && form.company_id) {
      await companiesStore.updateCompany(form.company_id, {
        size: form.size,
      })
    }

    fillForm()
  } catch (error: any) {
    pushNotification({
      title: t(
        `notifications.companies.${
          needCreateCompany ? 'create' : 'update'
        }_error`
      ),
      theme: 'destructive',
    })
    useLogError(error, true)
  } finally {
    internalProcessing.value = false
  }
}
</script>
